import { forwardRef, useImperativeHandle, useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import TreeItem from "../../TreeItem";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { adminService } from "../../../../../services/api/adminService";
import { useSidebarLayout } from "../../../../../contexts/SidebarLayoutContext";
import CustomSelect from "../../CustomSelect";

import { ReactComponent as RefreshIcon } from "../../../../../assets/icons/wrap.svg";

const transformToTree = (staffGroups, myGroups) => {
    if (!staffGroups) return [];
    return staffGroups.map((sGroup) => {
        const mGroup = (myGroups || []).find(m => m.groupSlug === sGroup.groupSlug);
        const combinedPermissions = [...(mGroup?.permissions || [])];
        sGroup.permissions?.forEach(sp => {
            if (!combinedPermissions.some(mp => mp._id === sp._id)) {
                combinedPermissions.push(sp);
            }
        });

        const children = combinedPermissions.map(p => ({
            id: p._id,
            name: p.name,
            checked: sGroup.permissions?.some(sp => sp._id === p._id) ?? false,
            slug: p.nameSlug
        }));

        return {
            id: sGroup.groupId || sGroup.groupSlug,
            name: sGroup.groupName,
            checked: children.length > 0 && children.some(c => c.checked),
            slug: sGroup.groupSlug,
            children
        };
    });
};

const UserPermissionTab = forwardRef(({ isReadOnly = false, staffId }, ref) => {
    const { token } = useContext(AuthContext);
    const { permissions: myPermissions } = useSidebarLayout();
    const [permissions, setPermissions] = useState([]);
    const [originalPermissions, setOriginalPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);


    const fetchStaffPermissions = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminService.get_staff_permission(token, staffId);
            if (res.success) {
                const treeData = transformToTree(res.data.groupPermissions, myPermissions);
                setPermissions(treeData);
                setOriginalPermissions(JSON.parse(JSON.stringify(treeData)));
                setHasChanges(false);
            }
        } catch (error) {
            console.error("Fetch staff permissions error:", error);
            toast.error("Không thể tải danh sách phân quyền");
        } finally {
            setLoading(false);
        }
    }, [token, staffId, myPermissions]);

    useEffect(() => {
        if (!token || !staffId) return;
        fetchStaffPermissions();
    }, [token, staffId, fetchStaffPermissions]);

    const toggleRecursive = (list, targetId) =>
        list.map((item) => {
            if (item.id === targetId) {
                const newChecked = !item.checked;
                return {
                    ...item,
                    checked: newChecked,
                    children: item.children?.map((child) => ({ ...child, checked: newChecked })),
                };
            }
            if (item.children) {
                const updatedChildren = toggleRecursive(item.children, targetId);
                return {
                    ...item,
                    children: updatedChildren,
                    checked: updatedChildren.length > 0 && updatedChildren.some((c) => c.checked),
                };
            }
            return item;
        });

    const handleToggle = (id) => {
        if (isReadOnly) return;
        setPermissions((prev) => {
            const next = toggleRecursive(prev, id);
            setHasChanges(JSON.stringify(next) !== JSON.stringify(originalPermissions));
            return next;
        });
    };

    const handleReset = () => {
        setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
        setHasChanges(false);
        toast.info("Đã hoàn tác các thay đổi");
    };

    const handleSave = async () => {
        if (!hasChanges) {
            toast.info("Không có thay đổi nào để lưu");
            return;
        }

        const payload = {
            customGroupPermissions: permissions
                .map(group => ({
                    group: group.id,
                    permissions: group.children.filter(p => p.checked).map(p => p.id)
                }))
                .filter(group => group.permissions.length > 0)
        };

        try {
            const res = await adminService.assign_permission(token, staffId, payload);
            if (res.success) {
                toast.success("Cập nhật phân quyền thành công!");
                setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
                setHasChanges(false);
            } else {
                toast.error(res.message || "Cập nhật thất bại");
            }
        } catch (error) {
            console.error("Assign permission error:", error);
            toast.error(error?.response?.data?.message || "Có lỗi xảy ra khi cập nhật phân quyền");
        }
    };

    useImperativeHandle(ref, () => ({
        handleReset,
        handleSave
    }));

    const availableGroups = (myPermissions || [])
        .filter(myGroup => !permissions.some(staffGroup => staffGroup.slug === myGroup.groupSlug))
        .map(group => ({
            value: group.groupId,
            label: group.groupName,
            original: group
        }));

    const handleAddGroup = (option) => {
        if (!option) return;
        const groupToAdd = option.original;
        const newGroup = {
            id: groupToAdd.groupId,
            name: groupToAdd.groupName,
            slug: groupToAdd.groupSlug,
            checked: true,
            children: groupToAdd.permissions?.map(p => ({
                id: p._id,
                name: p.name,
                slug: p.nameSlug,
                checked: true
            })) || []
        };
        setPermissions(prev => {
            const next = [...prev, newGroup];
            setHasChanges(JSON.stringify(next) !== JSON.stringify(originalPermissions));
            return next;
        });
        console.log(`Đã thêm nhóm: ${groupToAdd.groupName}`);
    };

    if (loading) return <div className="p-10 text-center text-gray-500">Đang tải phân quyền...</div>;

    return (
        <div className="flex flex-col gap-6 w-full">
            {!isReadOnly && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex flex-col sm:flex-row items-end gap-4">
                        <div className="flex-1 w-full sm:max-w-md">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Chức năng</label>
                            <CustomSelect
                                options={availableGroups}
                                placeholder="Chọn phân quyền"
                                height="52px"
                                controlClassName="rounded-lg"
                                onChange={handleAddGroup}
                                value={null}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={handleReset}
                            disabled={!hasChanges}
                            className={`h-[52px] px-3 rounded-xl bg-white border flex items-center gap-2 text-sm font-medium transition-all duration-200 ${hasChanges
                                ? "border-blue-600 text-blue-600 hover:bg-blue-50"
                                : "border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
                                }`}
                        >
                            <RefreshIcon className={`w-4 h-4 shrink-0 ${hasChanges ? "text-blue-600" : "text-gray-400"}`} />
                            Hoàn tác
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                    {permissions?.length > 0 ? (
                        permissions?.map((group) => (
                            <div
                                key={group.id}
                                className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                            >
                                <TreeItem item={group} onToggle={handleToggle} level={0} />
                            </div>
                        ))
                    ) : (
                        <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                            Chưa có phân quyền nào được gán
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default UserPermissionTab;
